import React, { FormEvent, useEffect, useImperativeHandle, useRef, useState } from "react";
import styles from "./CustomLocalSearchInput.module.scss";
import Text from "components/text/Text";
import { ResetButton } from "components/buttons/resetButton/ResetButton";
import { getAllClassNames, setParams } from "helpers";
import { Close } from "assets/icons";
import strings from "assets/strings";
import { useClickOutside } from "hooks";
import Popup from "components/popup/Popup";
import { SERACH_DOCTORS_API } from "assets/constants";
import { KeyValuePair } from "types";
import {
  ISearchPopup,
  SearchPopupRadioLocal,
} from "components/popup/searchPopupRadioLocal/SearchPopupRadioLocal";
import {
  SearchPopupLocal,
} from "components/popup/searchPopupLocal/SearchPopupLocal";
import settings from "assets/mergeSettings";
import { HighlightText } from "components/highlightText/HighlightText";
import RadioButtonCheckBox from "components/radioButton/RadioButtonCheckBox";

export type ControlledLocalSearchBoxProps = {
  title?: string;
  title2?: string;
  marginTop?: number;
  multiSelect?: boolean;
  isPopup?: boolean;
  disableList?: boolean;
  popupProps: ISearchPopup;
  placeholder?: string;
  attribute: string;
  serviceType?: string;
  queryParams?: KeyValuePair[];
  forceReset?: boolean;
  selectedSubBubbles?: { value: string; label: string; }[];
  setSelectedSubBubbles?: (subBubbles: string[]) => void;
  onContinue?(value: string, hasSubFields: boolean): void;
  onChange?(value: string, hasSubField?: boolean): void;
  enableFreeText?: boolean;
  onReset?: any;
  enableNotAutoComplete?: boolean;
  showRadioButtonCheckBox?: boolean;
  isEnabledRadioButton?: boolean;
  setIsEnabledRadioButton?: React.Dispatch<React.SetStateAction<boolean>>;
  
  
};

export interface ICustomLocalSearchInputRef {
  resetBubbles: () => void;
}

export const CustomLocalSearchInput = React.forwardRef<ICustomLocalSearchInputRef, ControlledLocalSearchBoxProps>(({
  title,
  title2,
  marginTop,
  multiSelect,
  isPopup,
  disableList,
  popupProps,
  placeholder,
  attribute,
  serviceType,
  queryParams,
  selectedSubBubbles,
  setSelectedSubBubbles,
  enableFreeText,
  onReset,
  onContinue,
  forceReset,
  onChange,
  enableNotAutoComplete = false,
  showRadioButtonCheckBox,
  isEnabledRadioButton,
  setIsEnabledRadioButton
}, ref) => {
  const [items, setItems] = useState<Array<{ value: string; label: string; hasSubField?: boolean }>>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [query, setQuery] = useState("");
  const [localPlaceHolder, setLocalPlaceHolder] = useState(placeholder);
  const [itemSelectedFromLit, setItemSelectedFromList] = useState(false);
  const [bubbles, setBubbles] = useState<Array<{ value: string; label: string; hasSubField?: boolean }>>([]);
  const [multiSelectParams, setMultiSelectParams] = useState<string[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const [id] = useState(`searchbox-${attribute}`);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const firstBubbleRef = useRef<HTMLDivElement>(null);
  const toListRef = useRef<any>(null);
  //const [isMobile, setIsMobile] = useState(window.innerWidth <= 991.98);
  const popupRef = useRef<HTMLDivElement>(null);
  const [popupKey, setPopupKey] = useState(0); // Add state to manage key for re-rendering

  useImperativeHandle(ref, () => ({
    resetBubbles: () => {
      setBubbles([]);
      onReset && onReset();
    },
  }));

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showDropdown) {
        setShowDropdown(false);
      }
    };


    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };


  }, [showDropdown]);

  // const handleResize = () => {
  //   setIsMobile(window.innerWidth < 992);
  // };

  // useEffect(() => {
  //   // Set up the event listener
  //   window.addEventListener('resize', handleResize);

  //   // Clean up the event listener on component unmount
  //   return () => {
  //     window.removeEventListener('resize', handleResize);
  //   };
  // }, []);


  useClickOutside(
    dropdownRef,
    () => {
      if (enableFreeText) {
        if (query) {
          if (!itemSelectedFromLit) {
            setItemSelectedFromList(false);
          } else {
            onChange && onChange(query);
            if (multiSelect) setBubbles([...bubbles, { value: query, label: query }]);
            else setBubbles([{ value: query, label: query }]);
          }
        }
      }
      !enableNotAutoComplete && setQuery("");
      setShowDropdown(false);
    },
    id
  );

  // Function to set focus on a specific dropdown item
  const setFocus = (index: number) => {
    const dropdownItem = document.getElementById(`dropdown-item-${index}`);
    if (dropdownItem) {
      dropdownItem.focus(); // Set focus to the dropdown element
    }
  };

  const handleCloseKeyDown = (event: React.KeyboardEvent<SVGSVGElement>, item: { value: string; label: string; hasSubField?: boolean }) => {
    if (event.key === "Enter") {
      onSelect(item);
    }
  };


  const handleClosePopup = () => {
    handleToggleVisibility();
    focusToButton();
  }

  const focusToButton = () => {
    toListRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showDropdown) {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prevIndex) => {
            const newIndex = prevIndex === null || prevIndex === items.length - 1 ? 0 : prevIndex + 1;
            setFocus(0); // Set focus to the new highlighted item
            return newIndex;
          });
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prevIndex) => {
            const newIndex = prevIndex === null || prevIndex === 0 ? items.length - 1 : prevIndex - 1;
            setFocus(newIndex); // Set focus to the new highlighted item
            return newIndex;
          });
          break;
        case "Enter":
          e.preventDefault();
          if (highlightedIndex !== null) {
            onSelect(items[highlightedIndex]);
          }
          break;
        case "Escape":
          setShowDropdown(false);
          break;
        default:
          break;
      }
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    const input = inputRef.current;
    const handleBackspace = (e: any) => {
      if (e.key === "Backspace" || e.key === "Delete") {
        if (input?.value) {
          input.value.slice(0, -1);
        }
        else {
          const lastBubble = bubbles?.slice(-1)[0];
          if (lastBubble) {
            onSelect(lastBubble);
            inputRef.current?.focus();
          }
        }
      }
    };
    input?.addEventListener("keydown", handleBackspace);
    return () => {
      input?.removeEventListener("keydown", handleBackspace);
    };
  }, [bubbles]);

  useEffect(() => {       const fetchOptions = async () => {
   
      let normalizedOptions: any[] = [];
      const regex = /^[a-zA-Z0-9\u0590-\u05FF\s\-"]+$/;
      if (!regex.test(query)) {
        normalizedOptions = [{ value: strings.globalStrings.noMatchedItems, label: strings.globalStrings.noMatchedItems }];
      }
      else {
        const response = await fetch(`https://${settings.searchApiUrl}${SERACH_DOCTORS_API}${serviceType ? `${serviceType}` : ''}${attribute}?${setParams(queryParams)}${queryParams ? `&key=${query}` : `?key=${query}`}`);
  
        const options: any[] = await response.json();
        localStorage.setItem(`${attribute}`, JSON.stringify(options));
        
        if (!options.length || !regex.test(query)) {
          normalizedOptions = [{ value: strings.globalStrings.noMatchedItems, label: strings.globalStrings.noMatchedItems }];
        }
        else {
          normalizedOptions = options.map(item => {
            return {
              value: typeof item === "object" ? item[popupProps?.keyToNormalize] : item,
              label: typeof item === "object" ? String(item[popupProps?.labelToNormalize]) : item,
              hasSubFields: item['subMedicalFieldName']?.length > 0 ? true : false
            };
          });
        }
      }

      setItems(normalizedOptions);
    }
    //const regex = /^[a-zA-Z0-9\u0590-\u05FF\s]+$/;
    if (query /*&& regex.test(query)*/) fetchOptions();
    //else setItems([{ value: strings.globalStrings.noMatchedItems, label: strings.globalStrings.noMatchedItems }, { value: strings.globalStrings.selectItemFromList, label: strings.globalStrings.selectItemFromList }]);
    setShowDropdown(!!query);
  }, [query]);

  useEffect(() => {
    if (forceReset) {
      setBubbles([]);
      setQuery("");
    }
  }, [forceReset]);

  const handleInputChange = (value: string) => {
    setQuery(value);
    enableNotAutoComplete && onChange && onChange(value)
    if (value || bubbles.length > 0) {
      setLocalPlaceHolder('');
    } else {
      setItems([]);
      setLocalPlaceHolder(placeholder);
    }
  };
  const handleToggleVisibility = () => { 
    if (popupRef.current) { 
      popupRef.current.classList.toggle('show'); 
      setShowPopup(prev => !prev); 
      setPopupKey(prevKey => prevKey + 1); 
      setTimeout(() => { 
        const firstInput = popupRef.current?.querySelector('input, [tabindex="-1"]'); 
        if (firstInput instanceof HTMLElement) {
           firstInput.focus(); } }, 100); } };

  useEffect(() => {
    setLocalPlaceHolder(bubbles.length === 0 ? placeholder : "");
  }, [bubbles, placeholder]);


  const handleOnChange = (queryParams: string) => {
    setLocalPlaceHolder('');
    onChange && onChange(queryParams)
  }

  const onSelect = (item: any) => {
    if (item.label === strings.globalStrings.noMatchedItems || item.label === strings.globalStrings.selectItemFromList) return;
    setItemSelectedFromList(true);
    
    inputRef.current?.focus();
    if (multiSelect) {
      const temp = [...bubbles];
      const index = temp.findIndex((bubble) => bubble.label === item.label);
      if (index !== -1) {
        temp.splice(index, 1);
        if (temp.length === 0) { setLocalPlaceHolder(placeholder); }
      }
      else temp.push(item);
      setBubbles(temp);
      const queryParam = [...multiSelectParams];
      const indexParam = temp.findIndex((bubble) => bubble === item.value);
      if (indexParam !== -1) queryParam.splice(indexParam, 1);
      else queryParam.push(item.value)
      setMultiSelectParams(queryParam);
      onChange && onChange(temp.map(temp => temp.value).join(","));
      setQuery("");
    } else {
      const tempParams: string[] = [];
      const temp = [...bubbles];
      const index = temp.findIndex((bubble) => bubble.label === item.label);
      if (index !== -1) {
        temp.splice(index, 1);
        onChange && onChange("");
        onReset && onReset();
        setBubbles([]);
      } else {
        tempParams.push(item.value);
        setBubbles([{ value: item.value, label: item.label, hasSubField: item.hasSubFields }]);
        onChange && onChange(item.value, item.hasSubFields);
      }
      setMultiSelectParams(tempParams);
      setQuery("");
    }
    setItemSelectedFromList(false);
    setTimeout(() => {
      setShowDropdown(false);
    });
    setHighlightedIndex(0);
    setTimeout(() => {
      if (firstBubbleRef.current) {
        firstBubbleRef.current.focus();
      }
    }, 0);
  };

  // const handleSubmit = (event: FormEvent) => {
  //   event.preventDefault();
  //   event.stopPropagation();
  // };

  const handleReset = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setItems([]);
    setQuery("");
    setLocalPlaceHolder(placeholder);
  };

  const handleSpanKeyDown = (e: React.KeyboardEvent, item: any, index: number) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        onSelect(item);
        break;
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = (index + 1) % items.length;
        setHighlightedIndex(nextIndex);
        setFocus(nextIndex);
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = (index - 1 + items.length) % items.length;
        setHighlightedIndex(prevIndex);
        setFocus(prevIndex);
        break;
    }
  };

     {/* Guy RadioButtonCheckBox start*/}
  useEffect(() => {
    if (isEnabledRadioButton === false) {
      setBubbles([]);
      setQuery("");
      setLocalPlaceHolder(placeholder);
      onReset && onReset();
    }
  }, [isEnabledRadioButton]);
   {/* Guy RadioButtonCheckBox end*/}
  return (
    <div
      className={getAllClassNames(["ais-SearchBox", styles.container])}
      style={{ marginTop }}
    >
      <div>
        {/* Guy RadioButtonCheckBox start*/}
          {showRadioButtonCheckBox ? (
        <div style={{ display: "flex", alignItems: "center" }}>
          <RadioButtonCheckBox
            label={undefined}
            id="addressRadioCity"
            value="addressRadioCity"
            isSelected={isEnabledRadioButton}
            name="addressRadioCity"
           onSelecteRadio={() => {
         if (setIsEnabledRadioButton) {
            setIsEnabledRadioButton((prev) => {
              return !prev;
            });
          }
        }}
        />
          {title && (
            <Text
              className={styles.title}
              text={title}
              labelFor={id} // Added labelFor for accessibility             
            />
          )}
        </div>
      ) : (
        title && (
          <Text
            className={styles.title}
            text={title}
            labelFor={id} // Added labelFor for accessibility             
          />
        )
      )}
         {/* Guy RadioButtonCheckBox End*/}
        {title2 && (
          
          <Text
            className={styles.title}
            text={title2}
            aria-label={title2} // Added aria-label for accessibility
          />
        )}
        {isPopup && (
          <Text
            ref={toListRef}
            isBtnDisabled={disableList}
            //onClick={disableList ? undefined : () => setShowPopup(true)}
            onClick={() => handleToggleVisibility()}
            className={styles.title2}
            text={strings.globalStrings.toList}
            ariaLabel={strings.globalStrings.toList}
            aria-pressed={showPopup} // Manage state for accessibility    
          //style={{ marginInlineEnd: 0, marginInlineStart: "auto" }}        
          />
        )}
      </div>

      <form
        action=""
        className={getAllClassNames([
          "ais-SearchBox-form",
          styles.form,
          showDropdown && styles.formActive,
        ])}
        noValidate
        onSubmit={handleSubmit}
        onReset={handleReset}
      >
        {bubbles.length > 0 && bubbles.map((item, index) => (
          <div
            aria-live="assertive" // Added aria-live for accessibility
            key={item.value}
            className={getAllClassNames([
              styles.bubble,
              "ais-RefinementList-item",
            ])}
          >
            <span
              title={item.label}
              ref={index === 0 ? firstBubbleRef : null}
              className={getAllClassNames([
                "ais-RefinementList-labelText",
                multiSelect ? styles.label : styles.labelSingle,
              ])}
            >
              {item.label}
            </span>
            <Close
              onClick={() => {
                onSelect(item);
              }}

              className={styles.icon}
              aria-label={`${strings.globalStrings.remove} ${item.label}`} // Added aria-label for accessibility
              role="button" // Added role for screen readers
              tabIndex={0} // Make the element focusable
              onKeyDown={(event) => handleCloseKeyDown(event, item)}
            />
          </div>
        ))}
        <input
          aria-autocomplete="list" aria-activedescendant="autocomplete-list" aria-owns="autocomplete-list" role="combobox"
          disabled={!enableFreeText}
          id={id}
          className="ais-SearchBox-input"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          placeholder={localPlaceHolder}
          spellCheck={false}
          maxLength={25}
          type="search"
          value={query}
          ref={inputRef}
          onKeyDown={handleKeyDown} // Add key down event listener
          onChange={(e: any) => handleInputChange(e.target.value)}
          aria-label={placeholder} // Added aria-label for accessibility    
          onBlur={() => {
            if (!items.some(item => item.label == query) && bubbles.length === 0) {
              setLocalPlaceHolder(placeholder);
            }
          }} //       
        />
        <ResetButton
          hidden={
            query === undefined || query.length === 0 || bubbles.length > 0
          }
          onReset={onReset}
          aria-label="Reset search" // Added aria-label for accessibility
        />
        {showDropdown && (
          <div
            aria-expanded={showDropdown ? 'true' : 'false'}
            id="autocomplete-list"
            ref={dropdownRef}
            className={styles.dropdown}
            role="listbox" // Added role for screen readers
          >
            {items &&
              items.filter((item) => !bubbles.some((bubble) => bubble.label === item.label))
                .map((item, index) => (
                  <span
                    key={`${item.value}_${index}`}
                    className={getAllClassNames([
                      styles.item,
                      highlightedIndex === index && styles.highlighted, // Add highlighted style
                    ])}
                    onClick={() => onSelect(item)}
                    onKeyDown={(e) => handleSpanKeyDown(e, item, index)} // Add key down event listener
                    id={`dropdown-item-${index}`} // Add an ID to each item
                    role="option" // Added role for screen readers
                    tabIndex={0} // Make the element focusable
                    aria-selected={highlightedIndex === index} // Manage state for accessibility
                  >
                    {items.some(obj => obj.label !== strings.globalStrings.noMatchedItems) ? <HighlightText query={query} text={item?.label} /> : item.label}
                  </span>
                ))
            }
          </div>
        )}
      </form>
      {popupProps && (
        <Popup ref={popupRef} onClose={() => handleClosePopup()} /*style={isMobile ? { paddingInline: '20px' } : {}}*/>
          {multiSelect ? (
            <SearchPopupLocal
            setOldBubbles={() => setBubbles([])}
              key={popupKey} // Add key prop to force re-render
              {...popupProps}
              popupBubbles={bubbles}
              attributeLocal={attribute}
              serviceType={serviceType}
              queryParams={queryParams}             
              onSubmit={(selectedVals: { value: string; label: string; }[], queryParams: string) => {
                onChange && handleOnChange(queryParams);
                setBubbles(selectedVals);
                handleToggleVisibility();
                toListRef.current?.focus();
              }}
            />
          ) : (
            <SearchPopupRadioLocal
              key={popupKey} // Add key prop to force re-render
              {...popupProps}
              subFieldName="subMedicalFieldName"
              popupBubbles={bubbles}
              subbubble={selectedSubBubbles}
              attributeLocal={attribute}
              serviceType={serviceType}
              queryParams={queryParams}              
              onSubmit={(selectedVals: { value: string; label: string; hasSubField: boolean }[], subVals: string[], queryParams: string) => {
                onChange && onChange(queryParams);
                onContinue && onContinue(selectedVals[0]?.value, selectedVals[0]?.hasSubField);
                setBubbles(selectedVals[0] ? [selectedVals[0]] : []);
                setSelectedSubBubbles && setSelectedSubBubbles(subVals);
                handleToggleVisibility();
                toListRef.current?.focus();
              }}
            />
          )}
        </Popup>
      )}
    </div>
  );
});